import { supabase } from '../supabase';

export interface Translation {
  id?: string;
  key: string;
  value: string;
  locale: string;
  created_at?: string;
  updated_at?: string;
}

export const translationsApi = {
  // Get all translations for a locale
  async getTranslations(locale: string = 'et') {
    const { data, error } = await supabase
      .from('cms_translations')
      .select('*')
      .eq('locale', locale)
      .order('key');

    return { data, error };
  },

  // Get specific translation
  async getTranslation(key: string, locale: string = 'et') {
    const { data, error } = await supabase
      .from('cms_translations')
      .select('*')
      .eq('key', key)
      .eq('locale', locale)
      .maybeSingle();

    return { data, error };
  },

  // Upsert translation
  async upsertTranslation(key: string, value: string, locale: string = 'et') {
    const { data, error } = await supabase
      .from('cms_translations')
      .upsert({ 
        key, 
        value, 
        locale 
      }, { 
        onConflict: 'key,locale',
        ignoreDuplicates: false 
      })
      .select()
      .single();

    return { data, error };
  },

  // Batch upsert translations
  async upsertTranslations(translations: Omit<Translation, 'id' | 'created_at' | 'updated_at'>[]) {
    const { data, error } = await supabase
      .from('cms_translations')
      .upsert(translations, { 
        onConflict: 'key,locale',
        ignoreDuplicates: false 
      })
      .select();

    return { data, error };
  },

  // Delete translation
  async deleteTranslation(key: string, locale: string = 'et') {
    const { data, error } = await supabase
      .from('cms_translations')
      .delete()
      .eq('key', key)
      .eq('locale', locale);

    return { data, error };
  },

  // Get translations as nested object
  async getTranslationsAsObject(locale: string = 'et') {
    const { data, error } = await this.getTranslations(locale);
    
    if (error || !data) {
      return { data: {}, error };
    }

    const translationsObject: Record<string, any> = {};
    
    data.forEach(translation => {
      const keys = translation.key.split('.');
      let current = translationsObject;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        
        // If the key doesn't exist or is not an object, initialize it as an empty object
        if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      
      // Try to parse JSON strings back to their original types
      let value = translation.value;
      if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // If parsing fails, keep as string
        }
      }
      
      current[keys[keys.length - 1]] = value;
    });

    return { data: translationsObject, error: null };
  },

  // Update translations from nested object
  async updateTranslationsFromObject(translationsObject: Record<string, any>, locale: string = 'et') {
    const flatTranslations: Omit<Translation, 'id' | 'created_at' | 'updated_at'>[] = [];

    const flatten = (obj: any, prefix: string = '') => {
      Object.keys(obj).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          flatten(obj[key], fullKey);
        } else {
          // Serialize arrays and objects as JSON strings
          let value: string;
          if (Array.isArray(obj[key]) || (typeof obj[key] === 'object' && obj[key] !== null)) {
            value = JSON.stringify(obj[key]);
          } else {
            value = String(obj[key]);
          }
          
          flatTranslations.push({
            key: fullKey,
            value,
            locale
          });
        }
      });
    };

    flatten(translationsObject);
    return await this.upsertTranslations(flatTranslations);
  }
};